/**
 * components/ui/index.tsx
 * Reusable primitive UI components.
 * All styled with inline styles to match Jötr brand — no Tailwind dependency.
 */

import React from 'react';
import { Loader } from 'lucide-react';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF',
  red:'#DC2626', green:'#16A34A',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ─── BUTTON ──────────────────────────────────────────────────────────────────
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  loading?: boolean;
}
export const Button: React.FC<ButtonProps> = ({ variant='primary', size='md', loading, children, style, ...rest }) => {
  const base: React.CSSProperties = {
    display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6,
    border:'none', cursor: rest.disabled||loading ? 'not-allowed' : 'pointer',
    fontFamily:font.body, fontWeight:700, borderRadius:10, transition:'opacity .15s, transform .1s',
    opacity: rest.disabled||loading ? 0.6 : 1,
    fontSize: size==='sm' ? 13 : 15,
    padding: size==='sm' ? '7px 14px' : '11px 22px',
  };
  const variants: Record<string, React.CSSProperties> = {
    primary: { background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, boxShadow:`0 4px 14px ${C.amber300}44` },
    ghost:   { background:'transparent', color:C.inkLight, border:`1.5px solid ${C.border}` },
    danger:  { background:'transparent', color:C.red, border:`1.5px solid ${C.red}44` },
  };
  return (
    <button style={{ ...base, ...variants[variant], ...style }} {...rest}>
      {loading && <Loader size={14} style={{ animation:'spin .7s linear infinite' }}/>}
      {children}
    </button>
  );
};

// ─── INPUT ────────────────────────────────────────────────────────────────────
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string; error?: string;
}
export const Input: React.FC<InputProps> = ({ label, error, style, ...rest }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
    {label && <label style={{ fontSize:12, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>{label}</label>}
    <input style={{
      height:40, padding:'0 12px', borderRadius:8, fontFamily:font.body, fontSize:14, color:C.ink,
      border:`1.5px solid ${error ? C.red : C.border}`, background:C.white, outline:'none',
      boxShadow: error ? `0 0 0 3px ${C.red}18` : 'none', ...style,
    }} {...rest}/>
    {error && <span style={{ fontSize:11, color:C.red, fontFamily:font.body }}>{error}</span>}
  </div>
);

// ─── BADGE ────────────────────────────────────────────────────────────────────
export const Badge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <span style={{ fontSize:10, fontWeight:700, padding:'2px 8px', borderRadius:20, background:color+'22', color, fontFamily:font.body, whiteSpace:'nowrap' }}>
    {children}
  </span>
);

// ─── SPINNER ──────────────────────────────────────────────────────────────────
export const Spinner: React.FC<{ size?: number }> = ({ size=20 }) => (
  <div style={{ display:'flex', justifyContent:'center', padding:24 }}>
    <Loader size={size} color={C.amber300} style={{ animation:'spin .7s linear infinite' }}/>
  </div>
);

// ─── EMPTY STATE ─────────────────────────────────────────────────────────────
export const EmptyState: React.FC<{ icon: React.ReactNode; title: string; desc: string; action?: React.ReactNode }> = ({ icon, title, desc, action }) => (
  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'60px 24px', textAlign:'center', gap:12 }}>
    <div style={{ width:56, height:56, borderRadius:16, background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', color:C.amber400 }}>{icon}</div>
    <div>
      <p style={{ fontSize:16, fontWeight:700, color:C.ink, fontFamily:font.head, marginBottom:6 }}>{title}</p>
      <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, maxWidth:280, lineHeight:1.6 }}>{desc}</p>
    </div>
    {action}
  </div>
);

// ─── SYNC INDICATOR ──────────────────────────────────────────────────────────
export const SyncIndicator: React.FC<{ status: 'idle'|'saving'|'saved'|'error'|'offline' }> = ({ status }) => {
  const map = {
    idle:    { label:'',            color:C.muted },
    saving:  { label:'Saving…',     color:C.amber400 },
    saved:   { label:'Saved',       color:C.green },
    error:   { label:'Save failed', color:C.red },
    offline: { label:'Offline',     color:C.muted },
  };
  const m = map[status];
  if (!m.label) return null;
  return <span style={{ fontSize:12, color:m.color, fontFamily:font.body, fontWeight:500 }}>{m.label}</span>;
};

// ─── AVATAR ───────────────────────────────────────────────────────────────────
export const Avatar: React.FC<{ name: string; size?: number }> = ({ name, size=32 }) => {
  const initials = name.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:font.head, fontWeight:800, fontSize:size*0.38, color:C.amber500, flexShrink:0, userSelect:'none' }}>
      {initials}
    </div>
  );
};

// ─── GLOBAL SPIN KEYFRAME ─────────────────────────────────────────────────────
export const GlobalSpin: React.FC = () => (
  <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
);
